import { GeneratingNameLabel } from '@src/components/common/GeneratingNameLabel';
import { InlineRenameField } from '@src/components/common/InlineRenameField';
import SparkleIcon from '@src/svgs/SparkleIcon';
import React, { useState } from 'react';
import styled from 'styled-components';

interface ClipPreviewTitleProps {
  /** The clip's title — a reel's name (PLAT-11213 DS1), any other clip's name. */
  name: string;
  /** An emptied field is the host's to ignore; a clip cannot be nameless (DS8). */
  onRename: (name: string) => void;
  /**
   * Absent where there are no Sections to read — a non-reel clip, or the flag
   * off (DS9). The rename above is not reel-specific and is always offered.
   *
   * Where it is offered, it appears only while the name is being edited: asking
   * the AI for one is the alternative to typing one, so it belongs beside the
   * open field rather than permanently next to the heading.
   */
  onGenerate?: () => void;
  isGenerating?: boolean;
  /** The last failure; it stands until the next run (E9). */
  error?: string;
}

/**
 * PLAT-11213 DS7 — the clip preview's heading: the name, the rename, and (for a
 * reel) the ask for a generated one.
 *
 * Its own component rather than more JSX in `SocialClipShareModal`: the modal is
 * 2.6k lines, and this is the surface the ticket's rules live on — that the
 * affordance appears only for a reel, that a run says it is running, that a
 * failure is visible and stands.
 */
export const ClipPreviewTitle: React.FC<ClipPreviewTitleProps> = ({
  name,
  onRename,
  onGenerate,
  isGenerating = false,
  error,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  /**
   * Bumped to remount the rename field, which is how it gets closed from out
   * here: the shared component owns its own open/closed state and has no
   * imperative close, and leaving it open across a generation would let the
   * draft the creator had typed commit over the name the run just landed.
   */
  const [fieldInstance, setFieldInstance] = useState(0);

  const handleGenerate = () => {
    setIsEditing(false);
    setFieldInstance(instance => instance + 1);
    onGenerate?.();
  };

  return (
    <>
      <Title>
        <InlineRenameField
          key={fieldInstance}
          value={name}
          placeholder="Untitled"
          ariaLabel="Rename this clip"
          testId="clip-preview-title"
          // A modal heading, not a list row: the pencil belongs beside the name
          // at the heading's own scale, the way the Content Studio story title
          // carries it (PLAT-11503).
          size="title"
          onCommit={onRename}
          onEditingChange={setIsEditing}
        />
      </Title>
      {/* Offered while the creator is changing the name, not at rest: the
          heading is quiet until they go to edit it, and the ask sits beside the
          open field as the alternative to typing one. */}
      {onGenerate && isEditing && (
        <GenerateNameButton
          type="button"
          disabled={isGenerating}
          data-testid="clip-preview-generate-name"
          // Keeps focus in the open field, so the blur-commit never fires and
          // this button is still mounted when the click lands. `handleGenerate`
          // closes the field deliberately instead.
          onMouseDown={event => event.preventDefault()}
          onClick={handleGenerate}
        >
          {/* The glyph this codebase already reads as "AI wrote this", the same
              one both naming menu items carry. */}
          <SparkleIcon width="16" height="16" strokeColor="currentColor" />
          Generate Name
        </GenerateNameButton>
      )}
      {isGenerating && (
        <Status>
          <GeneratingNameLabel />
        </Status>
      )}
      {!isGenerating && error && (
        <NameError
          role="status"
          title={error}
          data-testid="clip-preview-name-error"
        >
          {error}
        </NameError>
      )}
    </>
  );
};

/**
 * The heading is now `InlineRenameField`'s host rather than the text itself. The
 * field's `size="title"` sets its padding and pencil placement; the type scale is
 * supplied from the outside, and both of its states — resting text and open
 * input — take the same heading size so the name does not resize as the field
 * opens.
 *
 */
const Title = styled.h2`
  /* A flex container so the resting name's box hugs the name: the shared field
     leans on align-self: flex-start for that, which a block parent ignores,
     leaving the hover wash and the pencil stretched across the whole modal
     instead of sitting on the name the way the story title's do (PLAT-11503).
     The open input still fills the row — it carries its own width: 100%.
     Backtick-free on purpose: this sits inside a template literal. */
  display: flex;
  min-width: 0;
  width: 100%;
  /* Cancels the field's own left inset so the resting title sits optically
     where it always has: 7px of padding plus the 1px border (PLAT-11503). Left
     only — TitleContainer clips, so a negative margin on the vertical axis
     would take the top of the open input's glyphs with it. */
  margin: 0 0 0 -8px;
  color: var(--foreground);

  /* 18px on a 22px line, not the 20px @ 1.3 it carried: with the heading-scale
     field's 3px padding and 1px border above and below, 22 is what keeps the
     title row at the 30px the right pane's tab bar pads down to, so the two
     panes' dividers meet (ShareModalTabs, PLAT-11503). */
  button,
  input,
  span {
    font-size: 18px;
    line-height: 22px;
    font-weight: 700;
    color: var(--foreground);
  }
`;

/**
 * A Ghost control: naming the reel is a low-priority action beside the heading,
 * and `--primary` in a title row would read as the modal's main action.
 */
const GenerateNameButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  /* As tall as the row and no taller: at the 32px small-button height it was
     the tallest thing on the 30px title row, so the row grew by 2px the moment
     a rename opened and everything under it shifted (PLAT-11503). */
  align-self: stretch;
  padding: 0 var(--btn-px-sm);
  border: none;
  border-radius: var(--radius-input);
  background: none;
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background-color var(--transition-fast),
    color var(--transition-fast);

  &:hover:not(:disabled) {
    background: var(--border);
    color: var(--text-secondary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/**
 * `GeneratingNameLabel` carries no scale of its own — it takes the host's, which
 * is how one component serves a Section row, a Chapter heading and this. Beside
 * an 18px heading the ambient size reads as a second title, so the host supplies
 * the same metadata scale the Reel Editor's rows give it.
 */
const Status = styled.span`
  flex-shrink: 0;
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
`;

/** Whatever the endpoint said, truncated rather than widening the heading. */
const NameError = styled.span`
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--destructive);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
`;
